/**
 * BandTourPage — per-band tour profile.
 * /bands/:slug → hero (their actual flyer, even handwritten), tour date list,
 * IG link, embedded "Stay Safe on Tour" footer with the drink-spiking safety
 * bumper + Pierce County crisis info + link to /resources. Share button copies
 * a sharable URL that previews with the flyer + safety bumper.
 */
import { useEffect, useState } from "react";
import { useParams, Link, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, Calendar, MapPin, Instagram, Globe, Music, Shield,
  Share2, ExternalLink, Sparkles, AlertCircle, Check, Upload,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import BandTourMap from "../components/BandTourMap";
import { AdminInviteBar } from "../components/admin/AdminInviteBar";
import { InviteBandsPanel } from "../components/InviteBandsPanel";
import { AddToCalendar, OwnerCalendarSync } from "../components/ShowsCalendarSync";
import { InlineHoardPlayer } from "../player/InlineHoardPlayer";
import { useArtistFocus } from "../hooks/useArtistFocus";
import { getActiveCauses, trackCauseShare } from "../utils/causeShare";
import InteractiveArtistCard from "../components/InteractiveArtistCard";
import MusicCrossNav from "../components/MusicCrossNav";
import { Seo, breadcrumbJsonLd } from "../components/Seo";
import { canonicalUrl } from "../seo/routeMeta";
import { API, useAuth } from "../App";
import { Hoard } from "../components/Hoard";
import { useNorthStar } from "../hooks/useNorthStar";
import { NorthStarSection } from "../components/NorthStarSection";
import { PageMedia } from "../components/PageMedia";
import { VaultDropsShelf } from "../components/band/VaultDropsShelf";
import { ShowStudioBar } from "../components/band/ShowStudioBar";
import { BandDiscography, BandMembers, BandPhotoWall } from "../components/band/BandStage";
import { BandPasscodeAdmin } from "../components/band/BandPasscodeAdmin";
import { useHubEditor } from "../components/band/hubApi";
import { HubTicker } from "../components/band/HubTicker";
import { HubNav, HubSocialPills, ProPlusChip } from "../components/band/HubHeroBits";
import { HubAbout, HubBooking, HubPress, HubVideos } from "../components/band/HubSections";
import { HubPhotoWall } from "../components/band/HubPhotoWall";
import { HubEditBar, HubPlanToggle } from "../components/band/HubEditBar";

const PUBLIC_DOMAIN = "lookwashington.com";

// Signed-in owners of this band/artist see the flyer-drop CTA right above the map.
const OwnerAddShows = ({ slug }) => {
  const { user } = useAuth();
  const [owns, setOwns] = useState(false);
  useEffect(() => {
    if (!user) { setOwns(false); return; }
    axios.get(`${API}/my-tour/profiles`, { withCredentials: true })
      .then((r) => setOwns([...(r.data.bands || []), ...(r.data.artists || [])].some((p) => p.slug === slug)))
      .catch(() => setOwns(false));
  }, [user, slug]);
  if (!owns) return null;
  return (
    <>
    <Link to="/my-tour" className="block mb-4 rounded-2xl bg-[#1A1A1A] hover:bg-[#2E5C55] text-white px-5 py-4 text-center no-underline transition-colors shadow-lg" data-testid="owner-add-shows-btn">
      <span className="block font-black uppercase tracking-wide text-base sm:text-lg">Add shows — drop a flyer, confirm details &amp; it&rsquo;s live!</span>
      <span className="block text-xs text-white/75 mt-1">Only you see this button. Shows land on your page, the calendar and the map.</span>
    </Link>
    <InviteBandsPanel />
    <OwnerCalendarSync slug={slug} />
    </>
  );
};


const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });
};

const BandTourPage = () => {
  const SAFETY_BUMPER = useNorthStar().band_bumper;
  const { slug } = useParams();
  const wantsTourView = new URLSearchParams(useLocation().search).has("tour");
  useArtistFocus(slug);
  const [band, setBand] = useState(null);
  const [fullPage, setFullPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [shareDone, setShareDone] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const reload = () => setRefreshKey(k => k + 1);
  const { user: viewer } = useAuth();
  const isAdmin = ["admin", "magician"].includes(viewer?.role);
  const hubEditor = useHubEditor(slug, band?.hub_plan === "pro_plus");

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      // Fetch artist bio (touring_bands OR featured_artists) and the unified
      // shows list (events + tour_dates merged) in parallel.
      const showsPromise = axios.get(`${API}/artists/${slug}/shows`).catch(() => null);
      const featuredPromise = axios.get(`${API}/featured-artists`).then((r) => r.data || []).catch(() => []);
      // Artists with a built EPK page (profile_url) send /artists/:slug visitors there.
      featuredPromise.then((list) => {
        const fa = list.find((a) => a.slug === slug);
        if (!cancelled && fa?.profile_url && fa.profile_url !== `/artists/${slug}`) setFullPage(fa.profile_url);
      });
      try {
        // 1) Try the touring_bands collection first (Tier 2+ — has tour dates)
        const r = await axios.get(`${API}/touring-bands/${slug}`);
        const showsRes = await showsPromise;
        if (cancelled) return;
        const shows = showsRes ? showsRes.data : null;
        setBand({
          ...r.data,
          // Override with unified shows so out-of-state events appear on the map.
          upcoming_dates: shows ? shows.upcoming_dates : r.data.upcoming_dates,
          past_dates: shows ? shows.past_dates : r.data.past_dates,
          tour_name: r.data.tour_name || (shows && shows.tour_name) || null,
          // Prefer the live trove-derived flag from the unified endpoint —
          // featured_artists.has_trove_music is often stale/None even when
          // tracks exist. Falls back to the touring_bands flag if shows
          // endpoint failed.
          has_trove_music:
            shows && typeof shows.has_trove_music === "boolean"
              ? shows.has_trove_music
              : (r.data.has_trove_music === true ||
                 (r.data.tags || []).includes("trove-music")),
        });
        setNotFound(false);
      } catch {
        // 2) Fall back to the featured_artists collection (Tier 0/1 — bio
        //    + maybe music, no tour yet). This lets a Sam-Cori-style artist
        //    land on the same unified profile page as Walker, just with
        //    fewer sections rendered. The page is one canonical destination
        //    per artist regardless of tier.
        try {
          const match = (await featuredPromise).find(a => a.slug === slug);
          const showsRes = await showsPromise;
          const shows = showsRes ? showsRes.data : null;
          if (cancelled) return;
          if (match) {
            // Normalize featured_artists shape → touring_bands shape so the
            // rest of the component doesn't need to special-case it. Merge
            // in the unified shows list so dashboard-submitted events plot
            // on the artist's tour map.
            setBand({
              slug: match.slug,
              name: match.name,
              bio: match.bio,
              tagline: match.tagline,
              hero_image_url: match.photo_url || match.hero_image_url,
              flyer_url: null,
              instagram: match.instagram,
              website: match.website,
              tour_name: (shows && shows.tour_name) || null,
              tour_dates: [],
              upcoming_dates: shows ? shows.upcoming_dates : [],
              past_dates: shows ? shows.past_dates : [],
              has_trove_music:
                shows && typeof shows.has_trove_music === "boolean"
                  ? shows.has_trove_music
                  : (match.has_trove_music || false),
              tags: match.tags || [],
              _from_featured: true,
            });
            setNotFound(false);
          } else {
            setNotFound(true);
          }
        } catch {
          if (!cancelled) setNotFound(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [slug, refreshKey]);

  const share = async () => {
    // Share text uses the public lookwashington.com URL so the link works
    // outside of dev/preview hosts and tracks back to our public domain.
    const path = window.location.pathname;
    const url = `https://${PUBLIC_DOMAIN}${path}`;
    const text = `${band?.name}${band?.tour_name ? ` — ${band.tour_name}` : ""}\n\nOn tour with safety info. ${SAFETY_BUMPER}\nMore: ${url}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: band?.name, text, url });
      } else {
        await navigator.clipboard.writeText(text);
      }
      setShareDone(true);
      getActiveCauses().then((cs) => trackCauseShare(cs[0]));
      setTimeout(() => setShareDone(false), 2500);
    } catch (_) { /* user cancelled */ }
  };

  if (fullPage && !wantsTourView) return <Navigate to={fullPage} replace />;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF9F6]">
        <Navbar />
        <div className="text-center py-20 text-[#5C5C5C] animate-pulse">Loading…</div>
      </div>
    );
  }
  if (notFound || !band) {
    return (
      <div className="min-h-screen bg-[#FAF9F6]">
        <Navbar />
        <div className="max-w-2xl mx-auto p-10 text-center">
          <Music className="h-12 w-12 mx-auto mb-3 text-[#E2E0DC]" />
          <h1 className="text-2xl font-['Playfair_Display']">That artist page isn&apos;t here yet.</h1>
          <Link to="/artists/featured" className="text-[#2E5C55] underline mt-3 inline-block">← Back to Featured Artists</Link>
        </div>
      </div>
    );
  }

  const ig = band.instagram;
  const upcoming = band.upcoming_dates || [];
  const past = band.past_dates || [];
  const hero = band.flyer_url || band.hero_image_url;
  // Bands that have music in the Hoard (flagged on the touring band record
  // OR detected from a matching featured-artist slug) get a music-player card
  // rendered above their tour list — the visible bonus for contributing music.
  const hasTroveMusic = band.has_trove_music === true || (band.tags || []).includes("trove-music");
  const hasShows = (upcoming.length + past.length) > 0;

  // Progressive-disclosure profile tier — drives which sections render. One
  // canonical artist page; sections light up as the musician adds content.
  //   Tier 0 — just a profile (Sam Cori today)            → bio + socials
  //   Tier 1 — added music to the Hoard                    → + music player
  //   Tier 2 — added at least 1 show                        → + tour map + dates
  //   Tier 3 — added 3+ shows OR has a tour_name            → + tour-hub treatment
  const tier =
    (hasShows && (upcoming.length >= 3 || band.tour_name)) ? 3 :
    hasShows ? 2 :
    hasTroveMusic ? 1 : 0;
  // PRO+ hub (LOZEN): the band page is the band's whole website — always in the neon Volt look.
  const hub = band.hub_plan === "pro_plus";
  // page_style "neon" (LOZEN): black page, ON AIR cyan accents.
  const neon = band.page_style === "neon" || hub;
  const accent = neon ? "text-[#54E3FF]" : "text-[#FFB347]";

  const tourDates = (
    <section className="mb-10" data-testid="band-tour-dates">
      <div className="flex items-center gap-3 mb-4">
        <Calendar className="h-6 w-6 text-[#9B59B6]" />
        <h2 className="font-['Playfair_Display'] text-2xl sm:text-3xl font-semibold text-[#1A1A1A]">{hub ? "Shows" : "Tour dates"}</h2>
        <span className="text-xs text-[#5C5C5C]">{upcoming.length} upcoming · {past.length} past</span>
      </div>

      {upcoming.length === 0 && past.length === 0 ? (
        <div className="bg-white rounded-2xl border border-[#E2E0DC] p-8 text-center" data-testid="band-tour-dates-empty">
          <p className="text-[#5C5C5C] m-0">{hub ? `No shows posted right now — follow ${band.name} on Instagram or Facebook for the next one.` : "No dates posted yet. Follow on socials for updates."}</p>
        </div>
      ) : (
        <div className="grid gap-2.5">
          {groupResidencies(upcoming).map((s, i) => <DateRow key={`u-${s.stop_id || s.event_id || i}`} stop={s} state="upcoming" slug={slug} onUpdated={reload} />)}
          {past.length > 0 && (
            <div className="mt-3 mb-1 text-[10px] font-bold uppercase tracking-widest text-[#5C5C5C]">Past</div>
          )}
          {past.map((s, i) => <DateRow key={`p-${s.stop_id || i}`} stop={s} state="past" slug={slug} onUpdated={reload} />)}
        </div>
      )}
    </section>
  );

  return (
    <div className={`min-h-screen ${neon ? "bg-black band-neon" : "bg-[#FAF9F6]"}`} data-testid={`band-tour-${band.slug}`} data-style={band.page_style || "default"}>
      <Seo
        title={`${band.name} · THE WALL`}
        description={
          band.bio?.slice(0, 160) ||
          `${band.name} — artist on THE WALL. Tour dates, music, and how to support them.`
        }
        canonical={canonicalUrl(`/artists/${band.slug}`)}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "MusicGroup",
            name: band.name,
            description: band.bio || undefined,
            url: canonicalUrl(`/artists/${band.slug}`),
            image: band.hero_image_url || band.flyer_url || undefined,
            foundingLocation: band.hometown || undefined,
            sameAs: [band.website, band.spotify, band.bandcamp, band.instagram, band.facebook].filter(Boolean),
          },
          breadcrumbJsonLd([
            { name: "Home", url: canonicalUrl("/") },
            { name: "Artists", url: canonicalUrl("/artists") },
            { name: band.name, url: canonicalUrl(`/artists/${band.slug}`) },
          ]),
        ]}
      />
      <Navbar />
      <AdminInviteBar kind="music" slug={slug} />
      <ShowStudioBar slug={slug} name={band.name} />

      {/* HERO — the band's own flyer is the centerpiece */}
      <section className={neon ? "bg-black text-white border-b border-[#54E3FF]/30 shadow-[0_20px_80px_-40px_rgba(84,227,255,0.45)]" : "bg-gradient-to-br from-[#1A1A2E] via-[#3a2056] to-[#0d6b6b] text-white"} data-testid="band-hero">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
          <Link
            to="/artists/featured"
            className="text-xs text-white/60 hover:text-white inline-flex items-center gap-1 mb-4 no-underline"
            data-testid="band-back-link"
          >
            <ArrowLeft className="h-3 w-3" /> All Featured Artists
          </Link>

          <div className="grid md:grid-cols-[1fr_320px] gap-6 items-start">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className={`h-4 w-4 ${accent}`} />
                <span className={`text-xs font-bold tracking-widest uppercase ${accent}`} data-testid="band-eyebrow">{hub ? "Official band hub · THE WALL" : "Artist on THE WALL"}</span>
                {hub && <ProPlusChip />}
              </div>
              <h1 className={`font-['Playfair_Display'] text-4xl sm:text-5xl font-bold leading-tight ${neon ? "tracking-[0.06em] [text-shadow:0_0_24px_rgba(84,227,255,0.55)]" : ""}`} data-testid="band-name">
                {band.name}
              </h1>
              {band.tagline && <p className={`text-lg sm:text-xl font-['Playfair_Display'] italic mt-1 mb-3 ${neon ? "text-[#54E3FF]" : "text-white/85"}`} data-testid="band-tagline">{band.tagline}</p>}
              {band.tour_name && (
                <p className={`text-xl sm:text-2xl ${accent} font-['Playfair_Display'] italic mt-1 mb-4`}>{band.tour_name}</p>
              )}
              {band.bio && !hub && <p className="text-base text-white/80 leading-relaxed mb-5 max-w-2xl">{band.bio}</p>}
              {hub && band.hometown && <p className="text-sm font-bold uppercase tracking-[0.25em] text-white/50 mb-5" data-testid="band-hometown">{band.hometown}</p>}

              {/* Social pills */}
              <div className="flex gap-2 flex-wrap mb-5">
                {ig && (
                  <a href={ig} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 px-3 py-2 rounded-full text-sm no-underline text-white" data-testid="band-instagram">
                    <Instagram className="h-4 w-4" /> Instagram
                  </a>
                )}
                {hub && <HubSocialPills band={band} />}
                {band.website && (
                  <a href={band.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 px-3 py-2 rounded-full text-sm no-underline text-white">
                    <Globe className="h-4 w-4" /> Website
                  </a>
                )}
                {band.spotify && (
                  <a href={band.spotify} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 px-3 py-2 rounded-full text-sm no-underline text-white">
                    <Music className="h-4 w-4" /> Spotify
                  </a>
                )}
                <button
                  onClick={share}
                  className={`inline-flex items-center gap-2 ${neon ? "bg-[#54E3FF] text-black hover:bg-[#7eeaff]" : "bg-[#FFB347] text-[#1A1A2E] hover:bg-[#ffa325]"} px-4 py-2 rounded-full text-sm font-semibold no-underline border-0 cursor-pointer transition-colors`}
                  data-testid="band-share-button"
                >
                  {shareDone ? <><Check className="h-4 w-4" /> Copied!</> : <><Share2 className="h-4 w-4" /> Share</>}
                </button>
                <FeatureBandButton slug={slug} />
              </div>
              {hub && <HubNav band={band} hasPhotos={(band.gallery || []).length > 0 || !!hubEditor} />}
            </div>

            {/* Flyer thumbnail */}
            {hero && (
              <div className={`rounded-2xl overflow-hidden bg-black/30 shadow-2xl ring-2 ${neon ? "ring-[#54E3FF]/50" : "ring-white/10"}`}>
                <img src={hero} alt={band.name} className="w-full h-auto object-contain" data-testid="band-flyer" />
              </div>
            )}
          </div>
        </div>
      </section>
      {hub && <HubTicker lines={band.hub?.ticker || []} />}

      <NorthStarSection />

      {isAdmin && band.band_id && <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-8 -mb-6"><HubPlanToggle slug={slug} on={hub} onChanged={reload} /><BandPasscodeAdmin slug={slug} name={band.name} /></div>}
      <div className="band-body max-w-6xl mx-auto px-4 sm:px-6 pt-10">
        {hub && hubEditor && <HubEditBar band={band} as={hubEditor} onSaved={reload} />}
        {/* Music cross-nav — top-of-content anchor so visitors arriving on an
            individual band page can jump to Featured Artists / Whole Calendar
            / For Bands. */}
        <div className="mb-6" data-testid="band-tour-hoard-player">
          <p className="text-sm text-[#5C5C5C] mb-2 text-center italic">Listen to music for THE WALL — it keeps playing while you browse</p>
          <InlineHoardPlayer />
        </div>
        <div className="mb-8" data-testid="band-tour-cross-nav-wrap">
          <MusicCrossNav />
        </div>

        {/* TIER 0/1 PROMPT — when a band has neither music nor shows yet
            (Sam Cori today), or has music but no shows, surface a clear
            "BAND: Add Music for Fans" call-to-action so the page never
            looks empty AND so the artist (visiting their own page) has a
            one-tap path back to upload. Hidden for Tier 2+ where the tour
            map already carries the page. */}
        {tier <= 1 && !hub && (
          <section
            className="mb-10 rounded-2xl border-2 border-dashed border-[#FFB347]/60 bg-gradient-to-br from-[#FFF7E6] to-white p-6 sm:p-8"
            data-testid="band-tier-prompt"
          >
            <div className="flex flex-wrap items-center gap-4">
              <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#FFB347]/20">
                <Music className="h-6 w-6 text-[#E76F51]" />
              </span>
              <div className="flex-1 min-w-[220px]">
                <span className="block text-[10px] font-bold uppercase tracking-[0.25em] text-[#E76F51] mb-1">
                  Tier {tier} · {tier === 0 ? "Just getting started" : "Music up, tour next"}
                </span>
                <h2 className="font-['Playfair_Display'] text-2xl sm:text-3xl font-bold text-[#1A1A1A] leading-tight m-0">
                  BAND: Add {tier === 0 ? "Music" : "Shows"} for Fans
                </h2>
                <p className="text-sm text-[#5C5C5C] m-0 mt-1.5 max-w-xl">
                  {tier === 0
                    ? `Drop a track or two and unlock ${band.name}'s music player on this page — fans can listen while they browse, and your shows surface on the contributors-only calendar filter.`
                    : `${band.name} has music up — add a show or tour date and unlock the interactive tour map + "On Tour" badge.`}
                </p>
              </div>
              <Link
                to="/dashboard"
                className="inline-flex items-center gap-2 bg-[#E76F51] hover:bg-[#d6593b] text-white font-bold uppercase tracking-wider text-xs px-5 py-3 rounded-xl no-underline shadow-lg whitespace-nowrap"
                data-testid="band-tier-prompt-cta"
              >
                Open dashboard <ArrowLeft className="h-3.5 w-3.5 rotate-180" />
              </Link>
            </div>
          </section>
        )}

        <VaultDropsShelf slug={slug} name={band.name} isAdmin={isAdmin} />
        {!hub && <BandMembers members={band.members || []} />}
        {hub && <div id="shows" className="scroll-mt-24" />}

        {/* TOUR MAP — moved to the top per user request so fans can scout
            "what's near me?" the instant they land on a band's page. Clickable
            pins open a popup with date / venue / time / show notes. The
            section header switches to TOUR-mode only when the artist has
            explicitly set a tour_name; otherwise it reads "Where they're
            playing" so a one-show local band still gets the map without
            the "tour" framing being misleading. */}
        {(upcoming.length + past.length) > 0 && (
          <section className="mb-10" data-testid="band-tour-map-section">
            <div className="mb-3">
              {band.tour_name ? (
                <>
                  <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#9B59B6]" data-testid="band-tour-header-mode-tour">
                    TOUR · {band.tour_name}
                  </span>
                  <h2 className="font-['Playfair_Display'] text-2xl sm:text-3xl font-semibold text-[#1A1A1A] leading-tight m-0 mt-1">
                    Browse {band.name}&rsquo;s tour by what&rsquo;s near you
                  </h2>
                </>
              ) : (
                <>
                  <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#9B59B6]" data-testid="band-tour-header-mode-shows">
                    Where they&rsquo;re playing
                  </span>
                  <h2 className="font-['Playfair_Display'] text-2xl sm:text-3xl font-semibold text-[#1A1A1A] leading-tight m-0 mt-1">
                    {band.name}&rsquo;s shows &mdash; browse by what&rsquo;s near you
                  </h2>
                </>
              )}
              <p className="text-sm text-[#5C5C5C] m-0 mt-1">
                Tap any pin for date, venue and show details. The {" "}
                <span className="inline-flex items-center gap-1 align-middle"><span className="w-2.5 h-2.5 rounded-full bg-[#FFB347] inline-block" /> gold star</span> {" "}
                is the next show.
              </p>
            </div>
            <OwnerAddShows slug={slug} />
            <div className="mb-3"><AddToCalendar slug={slug} /></div>
            <BandTourMap stops={upcoming} bandName={band.name} />
            {upcoming.length === 0 && <p className="text-xs text-[#5C5C5C] mt-2">No upcoming shows on the map yet — past shows are listed below.</p>}
          </section>
        )}
        {hub && tourDates}

        {/* HOARD MUSIC PLAYER — only renders for bands flagged as having music
            in the Hoard. The big visual differentiator vs. tour-only bands. */}
        {hasTroveMusic && (
          <section className="mb-10" data-testid="band-trove-music-section">
            <div className="rounded-2xl bg-gradient-to-br from-[#FFF7E6] via-white to-[#F5F0FA] border-2 border-[#FFB347]/30 p-5 sm:p-7 shadow-md">
              <div className="flex items-center gap-3 mb-2">
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#FFB347]/20">
                  <Music className="h-5 w-5 text-[#9B59B6]" />
                </span>
                <div>
                  <span className="block text-[10px] uppercase tracking-[0.25em] font-bold text-[#E76F51]">
                    Bonus · <Hoard /> Artist Node
                  </span>
                  <h2 className="font-['Playfair_Display'] text-2xl sm:text-3xl font-semibold text-[#1A1A1A] leading-tight m-0">
                    Hear {band.name}&apos;s music while you scroll the tour.
                  </h2>
                </div>
              </div>
              <p className="text-sm text-[#5C5C5C] mb-5 max-w-2xl">
                {band.name} contributed music to the Hoard on THE WALL. Fans landing on this page can
                <strong> press play, hear the songs, and follow the tour — all without leaving</strong>.
                This is the difference an Artist Node makes.
              </p>
              <InteractiveArtistCard slug={band.slug} />
            </div>
          </section>
        )}

        {!hub && !band._from_featured && <BandPhotoWall slug={slug} photos={band.gallery || []} isAdmin={isAdmin} onChanged={reload} />}
        {!hub && <BandDiscography records={band.discography || []} />}
        {hub && (
          <>
            <HubAbout band={band} />
            <HubVideos videos={band.hub?.videos || []} name={band.name} />
            <HubPress press={band.hub?.press || []} />
            <HubBooking band={band} />
          </>
        )}

        {/* PROMOTED BY THE WALL — a permanent visual archive of shows we
            actively promoted for this artist. After a show date passes, the
            calendar removes the live event (correct behaviour — don't
            advertise past dates), but the artist still deserves to see "the
            flyer THE WALL made for me" on their page. Each entry is just a
            visual + caption — NO calendar link, NO buy-tickets CTA. Data
            comes from `band.wall_promoted_history` seeded in
            `seed_touring_bands.py`. */}
        {Array.isArray(band.wall_promoted_history) && band.wall_promoted_history.length > 0 && (
          <section className="mb-10" data-testid="band-wall-promoted-history">
            <div className="flex items-center gap-3 mb-4">
              <Sparkles className="h-6 w-6 text-[#FFB347]" />
              <div>
                <h2 className="font-['Playfair_Display'] text-2xl sm:text-3xl font-semibold text-[#1A1A1A] leading-tight m-0">
                  Promoted by <span className="text-[#FFB347]">THE WALL</span>
                </h2>
                <p className="text-sm text-[#5C5C5C] m-0 mt-1">
                  A keepsake archive — these flyers stay even after the date passes.
                </p>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {band.wall_promoted_history.map((entry, i) => (
                <figure
                  key={`promo-${entry.seed_key || i}`}
                  className="group relative bg-white rounded-2xl border-2 border-[#FFB347]/30 overflow-hidden shadow-md hover:shadow-xl hover:border-[#FFB347]/60 transition-all"
                  data-testid={`wall-promoted-flyer-${entry.seed_key || i}`}
                >
                  <div className="aspect-[3/4] overflow-hidden bg-[#1A1A2E]">
                    <img
                      src={entry.flyer_url}
                      alt={`${band.name} — ${entry.date_label} at ${entry.venue}`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                      loading="lazy"
                    />
                  </div>
                  <figcaption className="p-4">
                    <span className="block text-[10px] font-bold uppercase tracking-widest text-[#9B59B6] mb-1">
                      {entry.date_label}
                    </span>
                    <h3 className="font-['Playfair_Display'] text-lg font-semibold text-[#1A1A1A] leading-tight m-0">
                      {entry.title || `Live at ${entry.venue}`}
                    </h3>
                    {entry.venue && (
                      <p className="text-sm text-[#5C5C5C] m-0 mt-1">{entry.venue}{entry.city ? ` · ${entry.city}` : ""}</p>
                    )}
                    {entry.note && (
                      <p className="text-xs italic text-[#5C5C5C] m-0 mt-2">{entry.note}</p>
                    )}
                  </figcaption>
                </figure>
              ))}
            </div>
          </section>
        )}

        {/* TOUR DATES */}
        {!hub && tourDates}
      </div>

      {hub && <HubPhotoWall slug={slug} photos={band.gallery || []} editor={hubEditor} onChanged={reload} />}

      <div className={`band-body max-w-6xl mx-auto px-4 sm:px-6 pb-10 ${hub ? "pt-14" : ""}`}>
        {/* STAY SAFE ON TOUR — the resource footer */}
        <section
          className="rounded-2xl border-2 border-[#FFB347]/40 bg-gradient-to-br from-[#FFF7E6] to-[#F4F8F6] p-6 sm:p-8 mb-10"
          data-testid="band-safety-footer"
        >
          <div className="flex items-start gap-3 mb-4">
            <Shield className="h-6 w-6 text-[#2E5C55] flex-shrink-0 mt-0.5" />
            <div>
              <span className="text-[10px] font-bold uppercase tracking-widest text-[#E76F51]">Stay safe on tour</span>
              <h3 className="font-['Playfair_Display'] text-2xl font-semibold text-[#1A1A1A] mt-1 mb-2 italic leading-tight">
                {SAFETY_BUMPER}
              </h3>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3 mb-5">
            <SafetyLine icon="→" tone="#C0392B" body={<><strong>Life-threatening emergency</strong> → <a href="tel:911" className="text-[#2E5C55] underline font-semibold">911</a></>} />
            <SafetyLine icon="→" tone="#C0392B" body={<><strong>Suicide &amp; Crisis Lifeline</strong> → <a href="tel:988" className="text-[#2E5C55] underline font-semibold">988</a></>} />
            <SafetyLine icon="→" tone="#E76F51" body={<><strong>Pierce County Crisis Line (24/7)</strong> → <a href="tel:18005767764" className="text-[#2E5C55] underline font-semibold">1-800-576-7764</a></>} />
            <SafetyLine icon="→" tone="#E76F51" body={<><strong>Sexual Assault Center</strong> → <a href="tel:18007567273" className="text-[#2E5C55] underline font-semibold">1-800-756-7273</a></>} />
            <SafetyLine icon="→" tone="#5B8FB9" body={<><strong>Crisis text:</strong> text <span className="text-[#2E5C55] font-semibold">HOME</span> to <a href="sms:741741?body=HOME" className="text-[#2E5C55] underline">741741</a></>} />
            <SafetyLine icon="→" tone="#5B8FB9" body={<>At a venue? Tell the bartender first — most are trained to step in safely.</>} />
          </div>

          <Link
            to="/resources"
            className="inline-flex items-center gap-2 bg-[#2E5C55] hover:bg-[#234a44] text-white font-semibold px-5 py-3 rounded-xl no-underline"
            data-testid="band-resources-cta"
          >
            Find more in the Resource Library <ExternalLink className="h-4 w-4" />
          </Link>
        </section>

        {/* SHARE STRIP — encourages bands to repost */}
        <section className="mb-12 bg-white rounded-2xl border border-[#E2E0DC] p-6 flex items-center gap-4 flex-wrap" data-testid="band-share-strip">
          <div className="flex-1 min-w-[200px]">
            <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#1A1A1A] m-0">Share this tour →</h3>
            <p className="text-sm text-[#5C5C5C] m-0 mt-1">Every share carries the safety bumper to a new audience.</p>
          </div>
          <button
            onClick={share}
            className="inline-flex items-center gap-2 bg-[#9B59B6] hover:bg-[#7e3d9b] text-white font-semibold px-5 py-3 rounded-xl border-0 cursor-pointer transition-colors"
            data-testid="band-share-button-bottom"
          >
            {shareDone ? <><Check className="h-4 w-4" /> Copied!</> : <><Share2 className="h-4 w-4" /> Share</>}
          </button>
        </section>
      <PageMedia kind="artist" slug={slug} name={band?.name} />
      </div>

      <Footer />
    </div>
  );
};

const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
// Collapse a weekly residency (same title + venue + weekday, 3+ dates) into one "Every Wednesday" row.
export const groupResidencies = (stops) => {
  const key = (s) => `${(s.note || s.title || "").toLowerCase()}|${(s.venue || "").toLowerCase()}|${new Date(s.date + "T00:00:00").getDay()}`;
  const groups = new Map();
  stops.forEach((s) => { if (s.date) groups.set(key(s), [...(groups.get(key(s)) || []), s]); });
  const out = [], done = new Set();
  stops.forEach((s) => {
    const k = key(s), g = groups.get(k) || [];
    if (g.length < 3 || !(s.note || s.title)) { out.push(s); return; }
    if (done.has(k)) return;
    done.add(k);
    const last = g[g.length - 1];
    const until = new Date(last.date + "T00:00:00").toLocaleDateString([], { month: "short", day: "numeric" });
    out.push({ ...s, residency: { weekday: WEEKDAYS[new Date(s.date + "T00:00:00").getDay()], count: g.length, until } });
  });
  return out;
};

const DateRow = ({ stop, state, slug, onUpdated }) => {
  const isPast = state === "past";
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "magician";
  return (
    <div
      className={`bg-white border rounded-xl p-3 sm:p-4 flex items-center gap-4 flex-wrap ${isPast ? "border-[#F0EFEB] opacity-65" : "border-[#E2E0DC] hover:border-[#9B59B6]/40 transition-colors"}`}
      data-testid={`band-date-${stop.date}-${(stop.city || "").toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="text-center min-w-[64px]">
        <p className="text-[10px] font-bold uppercase tracking-widest text-[#9B59B6] m-0">
          {new Date(stop.date + "T00:00:00").toLocaleDateString([], { month: "short" })}
        </p>
        <p className="text-2xl font-bold text-[#1A1A1A] m-0 leading-none">
          {new Date(stop.date + "T00:00:00").getDate()}
        </p>
        <p className="text-[10px] text-[#5C5C5C] m-0">
          {new Date(stop.date + "T00:00:00").toLocaleDateString([], { weekday: "short" })}
        </p>
      </div>
      {stop.flyer_url && (
        <a
          href={stop.flyer_url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex-shrink-0 rounded-lg overflow-hidden border border-[#E2E0DC] hover:border-[#9B59B6]/50 transition"
          data-testid={`band-stop-flyer-${stop.stop_id || stop.date}`}
        >
          <img
            src={stop.flyer_url}
            alt={`${stop.city} flyer`}
            className="w-16 h-20 sm:w-20 sm:h-24 object-cover"
            loading="lazy"
          />
        </a>
      )}
      <div className="flex-1 min-w-[200px]">
        <p className="font-semibold text-[#1A1A1A] m-0 leading-tight">
          {stop.city}{stop.state ? `, ${stop.state}` : ""}{stop.country ? `, ${stop.country}` : ""}
        </p>
        {stop.venue && (
          <p className="text-sm text-[#5C5C5C] m-0 flex items-center gap-1 mt-0.5">
            <MapPin className="h-3 w-3" /> {stop.venue}
          </p>
        )}
        {(stop.time || stop.note) && (
          <p className="text-xs text-[#5C5C5C] m-0 mt-0.5">
            {stop.time && <span>{stop.time}</span>}
            {stop.note && <span className="italic ml-2">· {stop.note}</span>}
          </p>
        )}
        {stop.residency && (
          <p className="text-[11px] font-bold text-[#2E5C55] m-0 mt-1" data-testid={`band-residency-${(stop.venue || "").toLowerCase().replace(/\s+/g, "-")}`}>
            Every {stop.residency.weekday} · {stop.residency.count} dates through {stop.residency.until}
          </p>
        )}
      </div>
      {isAdmin && slug && stop.stop_id && (
        <StopFlyerUploader
          slug={slug}
          stop={stop}
          onUpdated={onUpdated}
        />
      )}
    </div>
  );
};

const StopFlyerUploader = ({ slug, stop, onUpdated }) => {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const inputId = `stop-flyer-input-${stop.stop_id}`;

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setErr("");
    setBusy(true);
    try {
      // 1) Upload the raw file — bulletproof pipeline returns a stored URL
      const form = new FormData();
      form.append("file", file);
      const up = await axios.post(`${API}/my-tour/upload-flyer`, form, {
        withCredentials: true,
      });
      const flyer_url = up.data?.flyer_url;
      if (!flyer_url) throw new Error("Upload succeeded but no URL returned");

      // 2) Attach that URL to this specific stop
      await axios.put(
        `${API}/my-tour/band/${slug}/stops/${stop.stop_id}`,
        {
          date: stop.date,
          city: stop.city,
          state: stop.state || "",
          country: stop.country || "",
          venue: stop.venue || "",
          time: stop.time || "",
          note: stop.note || "",
          flyer_url,
          ticket_url: stop.ticket_url || "",
        },
        { withCredentials: true }
      );
      onUpdated && onUpdated();
    } catch (ex) {
      setErr(ex?.response?.data?.detail || ex.message || "Upload failed");
    } finally {
      setBusy(false);
      e.target.value = ""; // allow re-pick of same file
    }
  };

  return (
    <div className="flex items-center gap-2">
      <label
        htmlFor={inputId}
        className={`inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider px-3 py-2 rounded-lg cursor-pointer border ${
          busy
            ? "bg-[#F0EEE9] border-[#E2E0DC] text-[#9B9B9B]"
            : "bg-white border-[#9B59B6]/40 text-[#9B59B6] hover:bg-[#9B59B6]/10"
        }`}
        title="Attach a flyer for this show"
        data-testid={`stop-flyer-upload-${stop.stop_id}`}
      >
        <Upload className="h-3.5 w-3.5" />
        {busy ? "Uploading…" : stop.flyer_url ? "Replace flyer" : "Upload flyer"}
      </label>
      <input
        id={inputId}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={onPick}
        disabled={busy}
      />
      {err && (
        <span className="text-[10px] text-[#C0392B]" title={err}>
          <AlertCircle className="h-3 w-3 inline mr-0.5" /> {err.slice(0, 40)}
        </span>
      )}
    </div>
  );
};

const SafetyLine = ({ icon, tone, body }) => (
  <div className="flex items-start gap-2 text-sm text-[#1A1A1A]">
    <span className="font-bold flex-shrink-0" style={{ color: tone }}>{icon}</span>
    <div>{body}</div>
  </div>
);

const FeatureBandButton = ({ slug }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [featured, setFeatured] = useState(null); // slug of currently featured band
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isAdmin) return;
    axios.get(`${API}/touring-bands/featured/current`)
      .then(r => setFeatured(r.data?.band?.slug || null))
      .catch(() => setFeatured(null));
  }, [isAdmin]);

  if (!isAdmin) return null;
  const isFeatured = featured === slug;

  const toggle = async () => {
    setBusy(true);
    try {
      if (isFeatured) {
        await axios.delete(`${API}/admin/touring-bands/featured/current`, { withCredentials: true });
        setFeatured(null);
      } else {
        await axios.patch(`${API}/admin/touring-bands/${slug}/feature`, {}, { withCredentials: true });
        setFeatured(slug);
      }
    } catch (e) {
      // no-op; error visibility handled by the caller — silent fail is OK
      // for a curator toggle.
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      onClick={toggle}
      disabled={busy}
      className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border-0 cursor-pointer transition-colors ${
        isFeatured
          ? "bg-[#FFB347] text-[#1A1A2E] hover:bg-[#ffa325]"
          : "bg-white/10 text-white hover:bg-white/20"
      }`}
      data-testid="feature-band-toggle"
      title="Show this tour on the Featured Artists page banner"
    >
      <Sparkles className="h-4 w-4" />
      {busy ? "Saving…" : isFeatured ? "Featured on Bands home" : "Feature on Bands home"}
    </button>
  );
};

export default BandTourPage;
